import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Outlet } from "@tanstack/react-router";
import { FileSearch02, Passport, SearchMd } from "@untitledui/icons";
import { useEffect, useMemo, useRef, useState } from "react";
import { z } from "zod";
import { Avatar } from "@/components/base/avatar/avatar";
import { Button } from "@/components/base/buttons/button";
import { AuditLogActionBadge } from "@/components/common/AuditLogActionBadge";
import { EmptyValue } from "@/components/common/EmptyValue";
import { TruncatedTooltipCellText } from "@/components/common/TruncatedTooltipCellText";
import CopyableId from "@/components/Molecules/CopyableId/CopyableId";
import {
	LogTableFilters,
	type LogTableFiltersState,
} from "@/components/Molecules/FilterDrawer/LogTableFilters";
import type { ColumnDef } from "@/components/Molecules/Table";
import { CustomTableWrapper } from "@/components/Molecules/Table/CustomTableWrapper";
import { TABLE_SEARCH_EMPTY_DESCRIPTION_KEY } from "@/components/Molecules/Table/TableSearchEmptyState";
import { Illustration } from "@/components/shared-assets/illustrations";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { useLogsFilterParams } from "@/hooks/use-logs-filter-params";
import { useTableSettings } from "@/hooks/use-table-settings";
import {
	type AuditLog,
	auditLogQueryOptions,
	useExportAuditLogs,
} from "@/services/audit-log";
import { downloadBlob, generateExportFilename } from "@/utils/download";
import { formatLongDate } from "@/utils/utils";

// Table settings constants
const TABLE_KEY = "admin.activity.orgLogs";
const DEFAULT_COLUMN_ORDER = [
	"id",
	"date",
	"eventType",
	"user.displayName",
	"organization.name",
	"userIpAddress",
	"userAgent",
	"source",
	"action",
	"fieldName",
	"oldValue",
	"newValue",
];
const DEFAULT_SORTING = [{ id: "date", desc: false }];
const DEFAULT_PAGE_SIZE = 10;

const normalizeColumnOrder = (columnOrder?: string[]) => {
	if (!Array.isArray(columnOrder) || columnOrder.length === 0) {
		return [...DEFAULT_COLUMN_ORDER];
	}

	const allowedColumns = new Set(DEFAULT_COLUMN_ORDER);
	const sanitized = columnOrder.filter((columnId) =>
		allowedColumns.has(columnId),
	);
	const unique = Array.from(new Set(sanitized));
	const missingDefaults = DEFAULT_COLUMN_ORDER.filter(
		(columnId) => !unique.includes(columnId),
	);

	return [...unique, ...missingDefaults];
};

// Serializable filter state for persistence (excludes date ranges)
interface PersistedFilters {
	eventTypes?: string[];
	sources?: string[];
}
const DEFAULT_FILTERS: PersistedFilters = {
	eventTypes: [],
	sources: [],
};

export const Route = createFileRoute(
	"/_authenticated/(admin-app)/_admin/admin/activity/logs/(orgLogs)/orgLogs",
)({
	component: RouteComponent,
	validateSearch: (search) => {
		return z
			.object({
				filterOpen: z.boolean().optional(),
			})
			.parse(search);
	},
});

function RouteComponent() {
	const { t } = useAppTranslation();
	const queryClient = useQueryClient();
	const [pageSize, setPageSize] = useState(10);
	const [pageIndex, setPageIndex] = useState(0);
	const [searchTerm, setSearchTerm] = useState("");
	const [isRefreshing, setIsRefreshing] = useState(false);
	const [filters, setFilters] = useState<LogTableFiltersState>({
		eventTypes: [],
		sources: [],
		timestamp: undefined,
	});
	const [sortParams, setSortParams] = useState({
		"OrderBy.Fields": ["date"],
		"OrderBy.Descending": false,
	});

	// Track if persisted settings have been applied
	const hasAppliedPersistedSettings = useRef(false);

	// Table settings persistence
	const {
		settings: tableSettings,
		isLoaded: settingsLoaded,
		saveSorting,
		saveColumnOrder,
		saveColumnVisibility,
		saveSearchTerm,
		saveFilters,
		savePageSize,
	} = useTableSettings<PersistedFilters>({
		tableKey: TABLE_KEY,
		defaultSorting: DEFAULT_SORTING,
		defaultColumnOrder: DEFAULT_COLUMN_ORDER,
		defaultSearchTerm: "",
		defaultFilters: DEFAULT_FILTERS,
		defaultPageSize: DEFAULT_PAGE_SIZE,
	});

	// Apply persisted settings (search, filters, page size) when config has loaded
	useEffect(() => {
		if (!settingsLoaded || hasAppliedPersistedSettings.current) return;
		hasAppliedPersistedSettings.current = true;

		if (tableSettings.searchTerm) {
			setSearchTerm(tableSettings.searchTerm);
		}
		if (tableSettings.filters) {
			setFilters((prev) => ({
				...prev,
				eventTypes: tableSettings.filters.eventTypes ?? [],
				sources: tableSettings.filters.sources ?? [],
			}));
		}
		if (
			Number.isFinite(tableSettings.pageSize) &&
			[10, 25, 50, 100].includes(tableSettings.pageSize)
		) {
			setPageSize(tableSettings.pageSize);
		}
	}, [
		settingsLoaded,
		tableSettings.searchTerm,
		tableSettings.filters,
		tableSettings.pageSize,
	]);

	const sortFieldMapping: Record<string, string[]> = {
		id: ["Id"],
		date: ["Date"],
		eventType: ["Action", "EntityType"],
		"user.displayName": ["User.DisplayName"],
		"organization.name": ["Organization.Name"],
		userIpAddress: ["UserIpAddress"],
		userAgent: ["UserAgent"],
		source: ["Source"],
		action: ["Action"],
		fieldName: ["FieldName"],
		oldValue: ["OldValue"],
		newValue: ["NewValue"],
	};
	const startIndex = pageIndex * pageSize + 1;
	const exportMutation = useExportAuditLogs();
	const filterParams = useLogsFilterParams(filters);

	const auditLogsCount = useQuery(
		auditLogQueryOptions.count({
			SearchTerm: searchTerm,
			...filterParams,
			...sortParams,
		}),
	);

	const unFilteredAuditLogsCount = useQuery(auditLogQueryOptions.count());

	const allAuditLogs = useQuery(
		auditLogQueryOptions.list(startIndex, pageSize, {
			SearchTerm: searchTerm,
			...filterParams,
			...sortParams,
		}),
	);

	const data = useMemo(() => {
		return allAuditLogs.data ?? [];
	}, [allAuditLogs.data]);

	const pageCount = useMemo(() => {
		if (!auditLogsCount.data) return 0;
		return Math.ceil(auditLogsCount.data / pageSize);
	}, [auditLogsCount.data, pageSize]);

	const handleRefresh = async () => {
		setIsRefreshing(true);
		try {
			await allAuditLogs.refetch();
		} finally {
			setIsRefreshing(false);
		}
	};

	const handleExport = (visibleColumns?: string[]) => {
		const totalCount = auditLogsCount.data ?? 0;
		if (totalCount === 0 || totalCount > 10000) return;

		// Transform column IDs for export - eventType maps to action and entityType
		const exportColumns = visibleColumns?.flatMap((col) =>
			col === "eventType" ? ["action", "entityType"] : [col],
		);

		exportMutation.mutate(
			{
				startIndex: 1,
				count: totalCount,
				params: {
					EntityTypes: "Organization",
					SearchTerm: searchTerm,
					...filterParams,
					...sortParams,
					columns: exportColumns?.join(","),
				},
			},
			{
				onSuccess: (blob) => {
					downloadBlob(blob, generateExportFilename("org_audit_logs"));
				},
			},
		);
	};

	const handleRowClick = (auditLog: AuditLog) => {
		console.log("auditLog", auditLog);
	};

	const handlePaginationChange = ({
		pageSize: newPageSize,
		pageIndex: newPageIndex,
	}: {
		pageSize: number;
		pageIndex: number;
	}) => {
		if (newPageSize !== pageSize) {
			setPageSize(newPageSize);
			setPageIndex(0);
			savePageSize(newPageSize);
		} else if (newPageIndex !== pageIndex) {
			setPageIndex(newPageIndex);
		}
	};

	const handleDebouncedSearchChange = (search: string) => {
		setSearchTerm(search);
		setPageIndex(0);
		// Save search term for persistence
		saveSearchTerm(search);
	};

	const handleFilterChange = (newFilters: LogTableFiltersState) => {
		setFilters(newFilters);
		// Save filters for persistence (only serializable fields)
		saveFilters({
			eventTypes: newFilters.eventTypes,
			sources: newFilters.sources,
		});
	};

	const auditLogColumns: ColumnDef<AuditLog>[] = [
		{
			id: "id",
			accessorKey: "id",
			header: t("AuditLog.Id", { useFieldInfo: true }),
			enableSorting: true,
			meta: {
				headerProps: {
					isRowHeader: true,
				},
			},
			cell: ({ row }) => {
				const user = row.original;
				return user.id ? (
					<div className="flex items-center gap-3">
						<Avatar
							alt={user.id}
							placeholder={<FileSearch02 className="size-6 text-gray-400" />}
							size="md"
						/>
						<div className="whitespace-nowrap">
							<p className="text-sm font-medium">
								{<CopyableId value={user.id} />}
							</p>
						</div>
					</div>
				) : (
					<EmptyValue />
				);
			},
		},
		{
			id: "date",
			accessorKey: "date",
			header: t("admin.activity.logs.columns.timestamp"),
			enableSorting: true,
			cell: ({ row }) => {
				const user = row.original;
				return (
					<span className="text-sm font-medium">
						{user.date ? (
							formatLongDate(user.date, { includeTime: true })
						) : (
							<EmptyValue />
						)}
					</span>
				);
			},
		},
		{
			id: "eventType",
			accessorKey: "eventType",
			header: t("admin.activity.logs.columns.eventType"),
			enableSorting: true,
			cell: ({ row }) => {
				const user = row.original;
				return (
					<span className="text-sm font-medium text-primary">
						{user.action && user.entityType ? (
							`${user.action} ${user.entityType}`
						) : (
							<EmptyValue />
						)}
					</span>
				);
			},
		},
		{
			id: "user.displayName",
			accessorKey: "user.displayName",
			header: t("admin.activity.logs.columns.actorUser"),
			enableSorting: true,
			cell: ({ row }) => {
				const user = row.original;
				return (
					<span className="text-sm font-medium text-primary">
						{user.user?.displayName ? (
							<span className="text-sm font-medium text-primary">
								{user.user?.displayName}
							</span>
						) : (
							<EmptyValue />
						)}
					</span>
				);
			},
		},
		{
			id: "organization.name",
			accessorKey: "organization.name",
			header: t("admin.activity.logs.columns.orgName"),
			enableSorting: true,
			cell: ({ row }) => {
				const user = row.original;
				return (
					<span className="text-sm font-medium text-primary">
						{user.organization?.name ? (
							<span className="text-sm font-medium text-primary">
								{user.organization?.name}
							</span>
						) : (
							<EmptyValue />
						)}
					</span>
				);
			},
		},
		{
			id: "userIpAddress",
			accessorKey: "userIpAddress",
			header: t("AuditLog.UserIpAddress", { useFieldInfo: true }),
			enableSorting: true,
			cell: ({ row }) => {
				const user = row.original;
				return (
					<span className="text-sm font-medium">
						{user.userIpAddress ? (
							<span className="text-sm font-medium">{user.userIpAddress}</span>
						) : (
							<EmptyValue />
						)}
					</span>
				);
			},
		},
		{
			id: "userAgent",
			accessorKey: "userAgent",
			header: t("AuditLog.UserAgent", { useFieldInfo: true }),
			enableSorting: true,
			cell: ({ row }) => (
				<TruncatedTooltipCellText value={row.original.userAgent} />
			),
		},
		{
			id: "source",
			accessorKey: "source",
			header: t("AuditLog.Source", { useFieldInfo: true }),
			enableSorting: true,
			cell: ({ row }) => {
				const user = row.original;
				return (
					<span className="text-sm font-medium">
						{user.source ? (
							<span className="text-sm font-medium">{user.source}</span>
						) : (
							<EmptyValue />
						)}
					</span>
				);
			},
		},
		{
			id: "action",
			accessorKey: "action",
			header: t("AuditLog.Action", { useFieldInfo: true }),
			enableSorting: true,
			cell: ({ row }) => <AuditLogActionBadge action={row.original.action} />,
		},
		{
			id: "fieldName",
			accessorKey: "fieldName",
			header: t("AuditLog.FieldName", { useFieldInfo: true }),
			enableSorting: true,
			cell: ({ row }) => {
				const user = row.original;
				return (
					<span className="text-sm font-medium">
						{user.fieldName ?? <EmptyValue />}
					</span>
				);
			},
		},
		{
			id: "oldValue",
			accessorKey: "oldValue",
			header: t("AuditLog.OldValue", { useFieldInfo: true }),
			enableSorting: true,
			cell: ({ row }) => {
				const user = row.original;
				return (
					<span className="text-sm font-medium">
						{user.oldValue ?? <EmptyValue />}
					</span>
				);
			},
		},
		{
			id: "newValue",
			accessorKey: "newValue",
			header: t("AuditLog.NewValue", { useFieldInfo: true }),
			enableSorting: true,
			cell: ({ row }) => {
				const user = row.original;
				return (
					<span className="text-sm font-medium">
						{user.newValue ?? <EmptyValue />}
					</span>
				);
			},
		},
	];

	const noLogs = (isSearchEmpty: boolean) => {
		const searchEmptyTitle = t(
			"admin.activity.logs.table.empty.noSearchResults",
		);

		return (
			<div className="flex flex-col items-center justify-center">
				<div className="relative flex items-center justify-center w-[480px] h-[480px]">
					<div className="relative flex flex-col items-center justify-center gap-4 text-center">
						{isSearchEmpty ? (
							<div className="flex h-12 w-12 items-center justify-center rounded-full bg-gray-100">
								<SearchMd className="size-6 text-gray-500" />
							</div>
						) : (
							<Illustration type="cloud" size="sm">
								<Passport className="size-6" />
							</Illustration>
						)}
						<div className="flex flex-col gap-1">
							{isSearchEmpty ? (
								searchEmptyTitle ? (
									<h2 className="font-semibold text-primary text-md">
										{searchEmptyTitle}
									</h2>
								) : null
							) : (
								<h2 className="font-semibold text-primary text-md">
									{t("admin.activity.logs.empty.title")}
								</h2>
							)}
							<p className="text-sm text-tertiary">
								{t(
									isSearchEmpty
										? TABLE_SEARCH_EMPTY_DESCRIPTION_KEY
										: "admin.activity.logs.empty.description",
								)}
							</p>
						</div>
					</div>
				</div>
			</div>
		);
	};

	useEffect(() => {
		if (!pageCount) return;

		const prefetchRange = 1;

		for (let i = 1; i <= prefetchRange; i++) {
			const nextPageIndex = pageIndex + i;
			if (nextPageIndex < pageCount) {
				const nextStartIndex = nextPageIndex * pageSize + 1;
				queryClient.prefetchQuery(
					auditLogQueryOptions.list(nextStartIndex, pageSize, {
						EntityTypes: "Organization",
						SearchTerm: searchTerm,
						...filterParams,
						...sortParams,
					}),
				);
			}
		}
	}, [
		pageIndex,
		pageSize,
		pageCount,
		queryClient,
		searchTerm,
		filterParams,
		sortParams,
	]);

	if (allAuditLogs.isError) {
		return (
			<div className="space-y-0">
				<div className="flex flex-col items-center justify-center py-12">
					<div className="text-center">
						<h3 className="text-sm font-medium text-error">
							{t("admin.activity.logs.errors.failedToLoad")}
						</h3>
						<p className="text-sm text-tertiary mt-1">
							{allAuditLogs.error?.message}
						</p>
						<Button
							size="sm"
							color="primary"
							onClick={handleRefresh}
							className="mt-3"
						>
							{t("common.actions.tryAgain")}
						</Button>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="space-y-0">
			<CustomTableWrapper
				tableKey={TABLE_KEY}
				// Data
				data={data}
				columns={auditLogColumns}
				// Display
				title={t("admin.activity.logs.table.title")}
				showHeaderPadding={false}
				zebraStriping
				// Loading States
				isLoading={allAuditLogs.isLoading}
				isFetching={allAuditLogs.isFetching}
				isRefreshing={isRefreshing}
				onRefresh={handleRefresh}
				// Pagination
				paginationMode="server"
				externalPaginationState={{ pageSize, pageIndex }}
				onPaginationChange={handlePaginationChange}
				pageCount={pageCount}
				rowCount={auditLogsCount.data}
				totalRowCount={unFilteredAuditLogsCount.data}
				// Sorting
				sortFieldMapping={sortFieldMapping}
				defaultSortField="date"
				onSortChange={({ fields, descending }) => {
					setSortParams({
						"OrderBy.Fields": fields,
						"OrderBy.Descending": descending,
					});
				}}
				// Filtering
				filterPlaceholder={t("admin.activity.orgLogs.table.search.placeholder")}
				filterValue={searchTerm}
				onDebouncedFilterChange={handleDebouncedSearchChange}
				filtersTrailingContent={
					<LogTableFilters value={filters} onChange={handleFilterChange} />
				}
				// Table Actions
				enableTableActions
				filterLayout="bar"
				filterBarType="workspace"
				filterBarClassName="pt-0"
				hasActiveFilters={Object.keys(filterParams).length > 0}
				onExportClick={handleExport}
				isExportLoading={exportMutation.isPending}
				// Features
				enableColumnReordering
				onRowClick={handleRowClick}
				enableColumnResizing
				// Persistence - Initial values from saved settings
				initialSortingState={tableSettings.sorting}
				initialColumnOrder={normalizeColumnOrder(tableSettings.columnOrder)}
				initialColumnVisibility={tableSettings.columnVisibility}
				settingsLoaded={settingsLoaded}
				// Persistence - Save callbacks
				onSortingStateChange={saveSorting}
				onColumnOrderChange={(order) => {
					saveColumnOrder(normalizeColumnOrder(order));
				}}
				onColumnVisibilityChange={saveColumnVisibility}
			/>

			{!allAuditLogs.isLoading && !allAuditLogs.isFetching && data?.length <= 0
				? noLogs(
						(unFilteredAuditLogsCount.data ?? 0) > 0 &&
							(auditLogsCount.data ?? 0) === 0,
					)
				: null}
			<Outlet />
		</div>
	);
}
